/**
 * App service for client-side API calls to /api/apps endpoints
 */

import { api } from '../utils/api-client'

/**
 * App record returned from the API
 */
export interface AppRecord {
  id: string
  workspaceId: string
  packageId: string | null
  userId: string
  name: string
  description: string | null
  icon: string | null
  category: string | null
  slug: string
  settings: Record<string, unknown>
  status: string | null
  installedVersion: string
  currentVersion: string
  agentCount: number | null
  createdAt: string | null
  updatedAt: string | null
  lastUsedAt: string | null
}

/**
 * App stats for dashboard cards
 */
export interface AppStats {
  agentCount: number
  chatCount: number
  messageCount: number
  knowledgeCount: number
  workflowCount: number
  taskCount: number
  lastActivityAt: string | null
}

/**
 * Agent info for activity items
 */
export interface ActivityAgentInfo {
  id: string
  name: string
  avatar: string | null
}

/**
 * Controls optional fetch behavior for app stats requests.
 */
export interface FetchAppStatsOptions {
  /**
   * Disables toast notifications for background stats hydration.
   * Used by the apps list to avoid one toast per failed app stat request.
   */
  silent?: boolean
}

/**
 * App activity feed item
 */
export interface AppActivityItem {
  type: 'chat' | 'agent' | 'workflow' | 'task' | 'knowledge'
  id: string
  title: string
  timestamp: string | null
  agent?: ActivityAgentInfo | null
  /** Status of the item (for tasks: PENDING, ACTIVE, COMPLETED, etc.) */
  status?: string | null
  /** Duration in milliseconds */
  duration?: number | null
  /** Priority for tasks */
  priority?: string | null
  /** Execution type for tasks */
  executionType?: string | null
}

/**
 * Data for creating a new app
 */
export interface CreateAppData {
  name: string
  description?: string
  icon?: string
  category?: string
  slug?: string
  settings?: Record<string, unknown>
}

/**
 * Data for updating an app
 */
export interface UpdateAppData {
  name?: string
  description?: string
  icon?: string
  category?: string
  slug?: string
  settings?: Record<string, unknown>
  status?: string
}

const API_OPTIONS = {
  requiresAuth: true,
  showErrorToast: true,
  showSuccessToast: false,
}

/**
 * Fetches all apps for the current workspace
 */
export async function fetchApps(): Promise<AppRecord[]> {
  const response = await api.get('/apps', API_OPTIONS)
  return response.data
}

/**
 * Fetches a single app by ID
 */
export async function fetchApp(appId: string): Promise<AppRecord> {
  const response = await api.get(`/apps/${appId}`, API_OPTIONS)
  return response.data
}

/**
 * Creates a new user-created app
 */
export async function createApp(data: CreateAppData): Promise<AppRecord> {
  const response = await api.post('/apps', data, {
    ...API_OPTIONS,
    showSuccessToast: true,
  })
  return response.data
}

/**
 * Updates an existing app
 */
export async function updateApp(appId: string, data: UpdateAppData): Promise<AppRecord> {
  const response = await api.put(`/apps/${appId}`, data, {
    ...API_OPTIONS,
    showSuccessToast: true,
  })
  return response.data
}

/**
 * Archives (soft-deletes) an app
 */
export async function deleteApp(appId: string): Promise<void> {
  await api.delete(`/apps/${appId}`, {
    ...API_OPTIONS,
    showSuccessToast: true,
  })
}

/**
 * Updates the default chat model for an app
 */
export async function updateAppChatModel(appId: string, model: string): Promise<void> {
  await api.patch(`/apps/${appId}/chat-model`, { model })
}

/**
 * Fetches aggregate stats for an app dashboard
 */
export async function fetchAppStats(
  appId: string,
  options: FetchAppStatsOptions = {}
): Promise<AppStats> {
  const response = await api.get(`/apps/${appId}/stats`, {
    ...API_OPTIONS,
    showErrorToast: !options.silent,
  })
  return response.data
}

/**
 * Fetches recent activity feed for an app
 */
export async function fetchAppActivity(appId: string, limit = 20): Promise<AppActivityItem[]> {
  const response = await api.get(`/apps/${appId}/activity?limit=${limit}`, API_OPTIONS)
  return response.data
}
